import { OnDataOptions } from '@apollo/client';
import { useRef } from 'react';
import { QNear, Waypoint } from '../../../util/machine-variable.utils';
import {
  RegisterAndSubscribeToMachineBindingSubscription,
  useRegisterAndSubscribeToMachineBindingSubscription,
} from '../../graphql/generated';

type TouchableInputPressCallback = (result: TouchableInputResult) => void;
interface TouchableInputConfig {
  input: string | number;
  delayPress?: number;
  delayLongPress?: number;
  onPress?: TouchableInputPressCallback;
  onLongPress?: TouchableInputPressCallback;
}

interface TouchableInputContext {
  input: string | number;
  inputTimeout: number;
}

export interface TouchableInputResult {
  state: boolean;
  time?: number;
  metadata: TouchableInputResultMetadata;
}

export interface TouchableInputResultMetadata {
  pose: Waypoint;
  qnear: QNear;
}

export const useTouchableInputBinding = (
  machineId: string,
  config: TouchableInputConfig,
  options?: { skip?: boolean }
): void => {
  const onPressRef = useRef<TouchableInputPressCallback>();
  const onLongPressRef = useRef<TouchableInputPressCallback>();

  onPressRef.current = config.onPress;
  onLongPressRef.current = config.onLongPress;

  const delayPress: number = config.delayPress || 100;
  const delayLongPress: number = config.delayLongPress || 1000;

  const context: TouchableInputContext = {
    input: config.input,
    inputTimeout: delayLongPress,
  };

  useRegisterAndSubscribeToMachineBindingSubscription({
    skip: options?.skip,
    variables: {
      input: {
        machineId,
        binding: {
          namespace: 'machine',
          type: 'digitalInputMonitor',
          machineId,
          context,
        },
      },
    },
    onData: (
      opts: OnDataOptions<RegisterAndSubscribeToMachineBindingSubscription>
    ) => {
      const data = opts.data.data?.registerAndSubscribeToMachineBindingResult
        ?.data as TouchableInputResult;

      if (data) {
        const { state, time } = data;

        if (state === true) {
          const shortPress = time ? time >= delayPress : false;
          const longPress = time ? time >= delayLongPress : false;

          if (longPress && typeof onLongPressRef.current === 'function') {
            onLongPressRef.current(data);
          } else if (shortPress && typeof onPressRef.current === 'function') {
            onPressRef.current(data);
          }
        }
      }
    },
  });
};
